import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout, Typography, message } from 'antd';
import { createTabChannel, getUserDetails, hasStudyViewingPermission, makePostCall } from '../../utils/helper';
import { launchViewerOnDualMonitor } from '../../utils/MonitorManager';
import type { OrderDetails } from './types';

import StudyQueue from './components/StudyQueue/StudyQueue';
import ReportEditor from './components/Editor/ReportEditor';

import './styles/reporting-page.css';

const { Content, Sider } = Layout;
const { Text } = Typography;

/**
 * Top-level route component for `/report`.
 *
 * Reads `order_ids` or `study_id` from search params, fetches order details,
 * and renders the study queue sidebar + report editor.
 *
 * Previously named `ReportingPage` in `reporting-page.tsx`.
 */
const ReportingWorkspace: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [ordersList, setOrdersList] = useState<OrderDetails[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetails | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const order_ids = searchParams.get('order_ids');
  const study_id = searchParams.get('study_id');
  const channel = createTabChannel();

  useEffect(() => () => channel.cleanup(), []);

  useEffect(() => {
    if (order_ids?.length) fetchOrderDetails(order_ids, null);
    else if (study_id) fetchOrderDetails('', study_id);
  }, [order_ids, study_id]);

  const fetchOrderDetails = async (orderIds: string, studyId: string | null) => {
    setLoading(true);
    try {
      const resp = await makePostCall('/orders-list-details', {
        order_ids: orderIds === '' ? [] : orderIds.split(','),
        study_uid: studyId,
        user_id: getUserDetails()?.username,
      });

      const ordData = resp?.data;
      [...(ordData.conflictingStudies || []), ...(ordData.reportingDisabledStudies || []), ...(ordData.signedOffStudies || [])].forEach(itm => {
        const reason = itm.opened_by ? `This study is being reported by ${itm.opened_by}.` : itm.reason || 'reporting disabled';
        message.warning(`${itm.study} - ${reason}`, 10);
      });

      setOrdersList(ordData?.data || []);

      if (!(ordData?.data?.length > 0)) {
        message.info('No studies available for reporting');
        return;
      }

      handleOrderSelect(ordData.data[0], true, -1);
      if (!studyId && orderIds !== '' && hasStudyViewingPermission(getUserDetails())) {
        launchViewerOnDualMonitor(ordData.data[0]?.ps_study_uid, ordData.data[0]);
      }
    } catch {
      message.error('Failed to fetch order details');
    } finally {
      setLoading(false);
    }
  };

  const handleOrderSelect = useCallback(async (order: OrderDetails, isInitial: boolean, index: number) => {
    const response = await makePostCall('/study-report-details', {
      user_id: getUserDetails()?.username,
      order_id: order?.pacs_order?.pacs_ord_id,
    });

    if (response?.data?.success) {
      setSelectedOrder(order);
      setSelectedIndex(index);
    } else {
      message.error(response?.data?.message);
      setSelectedOrder(null);
      setSelectedIndex(-1);
      return;
    }

    if (!isInitial && hasStudyViewingPermission(getUserDetails())) {
      channel.sendUrlUpdate(`/viewer/viewer?StudyInstanceUIDs=${order?.ps_study_uid}`);
    } else {
      setSelectedIndex(0);
    }
  }, []);

  const handlePostSaving = useCallback((status: string, _patDetails: OrderDetails) => {
    if (status !== 'signoff') return;
    if (selectedIndex === ordersList.length - 1) {
      message.info('Closing the window..');
      setTimeout(() => { window.open('about:blank', '_self'); window.close(); }, 3000);
    } else {
      message.info('Changing the study');
      setTimeout(() => {
        setSelectedOrder(ordersList[selectedIndex + 1]);
        setSelectedIndex(selectedIndex + 1);
      }, 1000);
    }
  }, [selectedIndex, ordersList]);

  return (
    <Layout className="reporting-page-layout">
      <Layout>
        <Sider
          width="16.25rem"
          collapsedWidth="3.125rem"
          collapsed={collapsed}
          onCollapse={setCollapsed}
          theme="light"
          className="reporting-page-sider"
        >
          <StudyQueue
            orders={ordersList}
            selectedIndex={selectedIndex}
            collapsed={collapsed}
            loading={loading}
            onSelect={(order, idx) => handleOrderSelect(order, false, idx)}
            onToggleCollapse={() => setCollapsed(c => !c)}
          />
        </Sider>
        <Layout className="content-layout">
          <Content>
            <div className="content-wrapper">
              {selectedOrder?.pacs_order ? (
                <ReportEditor
                  patientDetails={selectedOrder}
                  handlePostSaving={handlePostSaving}
                  cancel={() => { window.open('about:blank', '_self'); window.close(); }}
                />
              ) : (
                <div className="empty-state">
                  <Text type="secondary">Select an order to view/edit the report</Text>
                </div>
              )}
            </div>
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default ReportingWorkspace;
